import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MessageSquare, Phone, Mail, MapPin, CheckCircle2, XCircle,
  RefreshCw, Loader2, Database, Upload, FileSpreadsheet, Zap, Clock,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import GoogleCalendarCard from './GoogleCalendarCard';
import { SYNC } from '@/constants/testIds';

export default function SettingsTab() {
  const [comm, setComm] = useState({});
  const [sync, setSync] = useState({ fundle_enabled: false, last_run: null });
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [intervalDraft, setIntervalDraft] = useState('');
  const [fullHoursDraft, setFullHoursDraft] = useState('');
  const [savingSched, setSavingSched] = useState(false);
  const fileInputRef = React.useRef(null);

  const load = async () => {
    try {
      const [c, s, h, sch] = await Promise.all([
        api.get('/communication/status'),
        api.get('/sync/status'),
        api.get('/sync/history?limit=15'),
        api.get('/sync/scheduler'),
      ]);
      setComm(c.data); setSync(s.data); setHistory(h.data);
      setScheduler(sch.data);
      setIntervalDraft(String(sch.data?.delta_minutes ?? 5));
      setFullHoursDraft(String(sch.data?.full_hours ?? 6));
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const toggleScheduler = async (enabled) => {
    setSavingSched(true);
    try {
      const { data } = await api.post('/sync/scheduler', { enabled });
      setScheduler(data);
      toast.success(enabled ? 'Live sync enabled' : 'Live sync paused');
    } catch { toast.error('Failed to update scheduler'); }
    finally { setSavingSched(false); }
  };

  const saveInterval = async () => {
    const d = parseInt(intervalDraft, 10);
    const h = parseInt(fullHoursDraft, 10);
    if (!Number.isFinite(d) || d < 2 || d > 1440) {
      toast.error('Delta interval must be 2-1440 minutes'); return;
    }
    if (!Number.isFinite(h) || h < 1 || h > 48) {
      toast.error('Full sweep must be 1-48 hours'); return;
    }
    setSavingSched(true);
    try {
      const { data } = await api.post('/sync/scheduler', { delta_minutes: d, full_hours: h });
      setScheduler(data);
      toast.success(`Delta ${data.delta_minutes}m · full ${data.full_hours}h`);
    } catch { toast.error('Failed to update schedule'); }
    finally { setSavingSched(false); }
  };

  const runSync = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/sync/run');
      if (data.status === 'success') toast.success(`Sync complete`);
      else if (data.status === 'partial') toast.warning('Sync partial — see history');
      else toast.error(`Sync failed`);
      await load();
    } catch { toast.error('Failed to trigger sync'); }
    finally { setRunning(false); }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const results = [];
    for (const f of files) {
      try {
        const form = new FormData();
        form.append('file', f);
        const { data } = await api.post('/sync/excel-upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        results.push({ name: f.name, ok: true, stats: data.stats });
        toast.success(`${f.name}: ${data.stats.inserted} new + ${data.stats.updated} updated · ${data.stats.activities} notes`);
      } catch (err) {
        results.push({ name: f.name, ok: false, error: err?.response?.data?.detail || 'Failed' });
        toast.error(`${f.name} failed`);
      }
    }
    setUploadStats(results);
    setUploading(false);
    load();
  };

  return (
    <div className="space-y-6" data-testid={SYNC.page}>
      {/* Historical Excel upload */}
      <Card className="p-4 border-stone-200 border-dashed">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-emerald-50 flex items-center justify-center">
            <FileSpreadsheet className="w-4 h-4 text-emerald-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-stone-900">Historical Datasheet Upload</div>
            <div className="text-xs text-stone-500 mt-0.5">
              Drop one or many <code className="text-[10px] px-1 bg-stone-100 rounded font-mono">HC Lead Status</code> Excel files (19-column format). Every follow-up in the &quot;POC Follow up &amp; Date&quot; column becomes a timestamped note with the audiologist as author. Idempotent — re-uploading the same file simply refreshes it.
            </div>
            {uploadStats && (
              <div className="mt-3 space-y-1 text-xs">
                {uploadStats.map((r, i) => (
                  <div key={i} className={`px-2 py-1 rounded ${r.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
                    <strong>{r.name}</strong>{' '}
                    {r.ok ? (
                      <>· {r.stats.inserted} new · {r.stats.updated} updated · {r.stats.activities} notes {r.stats.errors?.length > 0 ? `· ${r.stats.errors.length} errors` : ''}</>
                    ) : (
                      <>· {r.error}</>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xlsx"
            onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
            className="hidden"
            data-testid="excel-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="sm"
            className="h-8 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="excel-upload-button"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            {uploading ? 'Ingesting…' : 'Upload Datasheets'}
          </Button>
        </div>
      </Card>

      {/* Fundle live sync scheduler */}
      <Card className="p-4 border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white" data-testid="fundle-live-sync-card">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-indigo-100 flex items-center justify-center">
            <Zap className="w-4 h-4 text-indigo-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold text-stone-900">Fundle Live Sync</div>
              {scheduler?.enabled ? (
                <Badge className="bg-emerald-100 text-emerald-800 text-[9px]" data-testid="live-sync-status-badge">
                  <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                  Running · delta {scheduler.delta_minutes}m · full {scheduler.full_hours}h
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[9px]" data-testid="live-sync-status-badge">
                  <XCircle className="w-2.5 h-2.5 mr-0.5" />Paused
                </Badge>
              )}
            </div>
            <div className="text-xs text-stone-500 mt-0.5">
              <strong>Delta pull</strong> (fast — new tickets only) every {scheduler?.delta_minutes ?? 5} min.
              <strong> Full sweep</strong> (masters + all-tickets refresh) every {scheduler?.full_hours ?? 6} h.
            </div>
            <div className="flex items-center gap-4 mt-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Switch
                  checked={!!scheduler?.enabled}
                  onCheckedChange={toggleScheduler}
                  disabled={savingSched}
                  data-testid="live-sync-toggle"
                />
                <span className="text-xs text-stone-700 font-medium">
                  {scheduler?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-stone-500" />
                <span className="text-[11px] text-stone-500 uppercase tracking-wide">Delta</span>
                <Input
                  type="number"
                  min={2}
                  max={1440}
                  value={intervalDraft}
                  onChange={(e) => setIntervalDraft(e.target.value)}
                  className="h-7 w-16 text-xs"
                  data-testid="live-sync-delta-input"
                />
                <span className="text-xs text-stone-500">min</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-stone-500 uppercase tracking-wide">Full</span>
                <Input
                  type="number"
                  min={1}
                  max={48}
                  value={fullHoursDraft}
                  onChange={(e) => setFullHoursDraft(e.target.value)}
                  className="h-7 w-16 text-xs"
                  data-testid="live-sync-full-input"
                />
                <span className="text-xs text-stone-500">h</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveInterval}
                  disabled={savingSched}
                  className="h-7 text-xs"
                  data-testid="live-sync-save-interval"
                >Save</Button>
              </div>
            </div>
            {scheduler?.jobs?.length > 0 && (
              <div className="text-[11px] text-stone-500 mt-2 font-mono space-y-0.5">
                {scheduler.jobs.map((j) => (
                  <div key={j.id}>
                    Next {j.id.replace('fundle-','').replace('-tick','')} run:{' '}
                    {j.next_run_at && j.next_run_at !== 'None' ? new Date(j.next_run_at).toLocaleString() : '—'}
                  </div>
                ))}
              </div>
            )}
            {scheduler?.last_delta_run && (
              <div className="text-[11px] text-stone-600 mt-1 font-mono">
                Last delta: {new Date(scheduler.last_delta_run.started_at).toLocaleString()} — {scheduler.last_delta_run.status}
                {scheduler.last_delta_run.stats?.new_tickets?.inserted !== undefined && (
                  <> · +{scheduler.last_delta_run.stats.new_tickets.inserted} new · {scheduler.last_delta_run.stats.new_tickets.updated} updated</>
                )}
              </div>
            )}
            {scheduler?.last_full_run && (
              <div className="text-[11px] text-stone-600 font-mono">
                Last full: {new Date(scheduler.last_full_run.started_at).toLocaleString()} — {scheduler.last_full_run.status}
              </div>
            )}
            {scheduler?.recent_errors?.length > 0 && (
              <div className="text-[11px] text-red-700 mt-1">
                Recent error: {scheduler.recent_errors[scheduler.recent_errors.length - 1].err?.slice(0, 120)}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Google Calendar migration */}
      <GoogleCalendarCard />

      {/* Fundle sync */}
      <Card className="p-4 border-stone-200">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-stone-100 flex items-center justify-center">
            <Database className="w-4 h-4 text-stone-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-stone-900">Fundle SOAP CRM</div>
              {sync.fundle_enabled ? (
                <Badge className="bg-emerald-100 text-emerald-800 text-[9px]" data-testid={SYNC.statusBadge}>
                  <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Configured
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[9px]" data-testid={SYNC.statusBadge}>
                  <XCircle className="w-2.5 h-2.5 mr-0.5" />Not configured
                </Badge>
              )}
            </div>
            <div className="text-xs text-stone-500 mt-0.5">
              Pull clinics, users, roles, groups, statuses, items, serials, sub-sources & tickets (17k+) into HearClear OS. Full migration bridge from SupportHub.
            </div>
            {sync.last_run && (
              <div className="text-[11px] text-stone-600 mt-2 font-mono">
                Last run: {new Date(sync.last_run.started_at).toLocaleString()} — {sync.last_run.status}
                {sync.last_run.errors?.length > 0 && (
                  <div className="text-red-700 mt-0.5">Errors: {sync.last_run.errors.slice(0, 2).join('; ')}</div>
                )}
              </div>
            )}
          </div>
          <Button
            onClick={runSync}
            disabled={running}
            size="sm"
            className="h-8 bg-stone-900 hover:bg-stone-800 text-white"
            data-testid={SYNC.triggerButton}
          >
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Run Full Sync
          </Button>
        </div>

        {history.length > 0 && (
          <div className="mt-4 border-t border-stone-100 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Sync History</div>
            <div className="space-y-1">
              {history.map((h) => (
                <div key={h.id} className="text-xs flex items-center gap-3 py-1 border-b border-stone-50 last:border-0">
                  <span className={
                    h.status === 'success' ? 'text-emerald-700' :
                    h.status === 'partial' ? 'text-amber-700' : 'text-red-700'
                  }>●</span>
                  <span className="tabular-nums text-stone-500">{new Date(h.started_at).toLocaleString()}</span>
                  <span className="font-semibold">{h.status}</span>
                  <span className="text-stone-400 truncate flex-1">
                    {Object.entries(h.stats || {}).map(([k, v]) => `${k}:${v.count ?? v.inserted ?? 0}`).join(' · ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Comm integrations */}
      <div>
        <h3 className="font-heading text-base font-bold text-stone-900 mb-2">Communication Integrations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <IntegrationCard icon={MessageSquare} name="WhatsApp" provider="Karix RCM" enabled={comm.whatsapp?.enabled} envKey="KARIX_API_KEY" />
          <IntegrationCard icon={Phone} name="Voice / Calling" provider="Not selected" enabled={false} envKey="—" />
          <IntegrationCard icon={Mail} name="Email" provider="Not selected" enabled={false} envKey="—" />
          <IntegrationCard icon={MapPin} name="Maps" provider="Not selected" enabled={false} envKey="—" />
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ icon: Icon, name, provider, enabled, envKey }) {
  return (
    <Card className="p-4 border-stone-200">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-stone-100 flex items-center justify-center">
          <Icon className="w-4 h-4 text-stone-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-stone-900">{name}</div>
            {enabled ? (
              <Badge className="bg-emerald-100 text-emerald-800 text-[9px]"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Active</Badge>
            ) : (
              <Badge variant="outline" className="text-[9px]"><XCircle className="w-2.5 h-2.5 mr-0.5" />Not activated</Badge>
            )}
          </div>
          <div className="text-xs text-stone-500 mt-0.5">{provider}</div>
          {envKey !== '—' && (
            <code className="mt-2 inline-block text-[10px] px-1.5 py-0.5 bg-stone-100 rounded font-mono text-stone-700">
              {envKey}
            </code>
          )}
        </div>
      </div>
    </Card>
  );
}
